import { onUnmounted, ref, watch } from 'vue'
import type { Ref } from 'vue'

/**
 * Returns a reactive boolean that tracks whether `elRef`'s element is
 * intersecting the viewport (using IntersectionObserver, threshold 0).
 * Starts as `true` (visible) so callers don't flash a "hidden" state before
 * the observer fires on mount.
 */
export function useIsVisible(elRef: Ref<HTMLElement | null>): Ref<boolean> {
  const isVisible = ref(true)
  let observer: IntersectionObserver | null = null

  watch(
    elRef,
    (el) => {
      observer?.disconnect()
      observer = null
      if (!el) return
      observer = new IntersectionObserver(
        ([entry]) => {
          isVisible.value = entry?.isIntersecting ?? true
        },
        { threshold: 0 },
      )
      observer.observe(el)
    },
    { flush: 'post', immediate: true },
  )

  onUnmounted(() => {
    observer?.disconnect()
    observer = null
  })

  return isVisible
}
